import { getOrgModerationConfig } from '@magicschool/business-logic';
import { createServiceRoleSupabaseClient } from '@magicschool/supabase/clients';
import { dataOnSuccess } from '@magicschool/supabase/utils';
import lunr from 'lunr';
import { getGlobalKeywordList } from '../backend-api/db/moderation';
import type { ModerationCategory } from './constants';
import type { LunrFunction } from './types';

// Can't get the d.ts files to work, so we'll just import them and cast them to the proper type
const lunrSpanish = require('lunr-languages/lunr.es') as LunrFunction;
const lunrMultiLanguage = require('lunr-languages/lunr.multi') as LunrFunction;
const lunrStemmer = require('lunr-languages/lunr.stemmer.support') as LunrFunction;

// Set up lunr.js with the necessary language support
lunrStemmer(lunr);
lunrMultiLanguage(lunr);
lunrSpanish(lunr);

export const calculateScale = (categoryScores: Record<ModerationCategory, number>, thresholds: Record<ModerationCategory, number>) => {
  const scaledResults = {} as Record<ModerationCategory, number>;

  for (const category in categoryScores) {
    const score = categoryScores[category as ModerationCategory];
    const threshold = thresholds[category as ModerationCategory];

    // Calculate the ratio
    const ratio = score / threshold;

    // Scale the ratio to a 1-10 scale
    const scaledScore = Math.min(Math.max(ratio * 10, 1), 10);

    // Store the scaled result
    scaledResults[category as ModerationCategory] = scaledScore;
  }

  return scaledResults;
};

interface LunrMetadata {
  text: {
    position: number[][];
  };
}

export async function getFilterList(roomStudentId: string): Promise<string[]> {
  const client = createServiceRoleSupabaseClient();

  // Get the org id from the room student
  const roomStudent = await client
    .from('room_students')
    .select('rooms(creator_org_id)')
    .eq('id', roomStudentId)
    .single()
    .then(dataOnSuccess);

  if (!roomStudent.rooms?.creator_org_id) {
    const globalKeywordList = await getGlobalKeywordList();
    return globalKeywordList.map((keyword) => keyword.keyword);
  }

  // Get the org moderation config
  const orgModerationConfig = await client
    .from('org_moderation_settings')
    .select('published_config, moderation_configs(*, keyword_lists(*, moderation_keywords(*)))')
    .eq('org_id', roomStudent.rooms?.creator_org_id)
    .maybeSingle()
    .then(dataOnSuccess);

  if (!orgModerationConfig || !orgModerationConfig.published_config) {
    const globalKeywordList = await getGlobalKeywordList();
    return globalKeywordList.map((keyword) => keyword.keyword);
  }

  // TODO: sort keyword lists by language
  const moderationKeywords =
    orgModerationConfig.moderation_configs?.keyword_lists?.moderation_keywords.map((keyword) => keyword.keyword) ?? [];

  return moderationKeywords.filter((keyword) => keyword) as string[];
}

export function detectFlaggedWords(text: string, flaggedKeywords: string[]): string[] {
  text = text.replace(/["/]+/g, ' ');

  // Index this text using lunr.js
  // This allows us to use stemming to find similar words (ie. hate in our filter matches hated, hating, etc)
  const indexedText = lunr(function () {
    // Use multiple languages
    this.use(lunr.multiLanguage('en', 'es'));

    this.metadataWhitelist = ['position'];

    this.field('text');
    this.add({ text });
  });

  // Search the indexed text for any words that match our filter list
  const searchResults = indexedText.search(flaggedKeywords.join(' '));
  if (searchResults.length === 0) return [];

  // For each result, pull out the word(s) that was matched
  const metadata = searchResults[0].matchData.metadata as Record<string, LunrMetadata>;
  const wordRoots = Object.keys(metadata);
  const results: string[] = [];
  for (const root of wordRoots) {
    for (const position of metadata[root].text.position) {
      results.push(text.slice(position[0], position[0] + position[1]));
    }
  }

  return results;
}

// We want to default autopause to on unless explicitly disabled for the org.
export async function isAutopauseSupported(roomId?: string | null): Promise<boolean> {
  if (!roomId) return true;
  const room = await createServiceRoleSupabaseClient()
    .from('rooms')
    .select('creator_org_id')
    .eq('id', roomId)
    .maybeSingle()
    .then(dataOnSuccess);
  return isAutopauseEnabledForOrg(room?.creator_org_id);
}

export async function isAutopauseEnabledForOrg(orgId?: string | null): Promise<boolean> {
  if (!orgId) return true;
  const orgConfig = await getOrgModerationConfig(orgId);
  return Boolean(orgConfig?.config?.autopause_enabled);
}
